import { ARRAY_ERROR, getIn } from 'final-form';

import { IKnmErknmFormInspector } from '../interfaces/IKnmErknmFormInspector';

/** Метки блока «Должностные лица…» в акте по типам мероприятий (заголовок блока + текст ошибки подписанта). */
export const KNO_INSPECTORS_DECISION_LABEL =
  'Должностные лица контрольного (надзорного) органа, участвовавшие в КНМ, СР';
export const KNO_INSPECTORS_PM_LABEL = 'Должностные лица контрольного (надзорного) органа, участвовавшие в ПМ';
export const KNO_INSPECTORS_KNM_LABEL =
  'Должностные лица контрольного (надзорного) органа, участвовавшие в контрольном (надзорном) мероприятии';

/** Метки раздела-источника должностных лиц паспорта по типам мероприятий. */
export const INSPECTORS_SECTION_DECISION_LABEL = 'Сведения о должностных лицах, участвующих в КНМ, СР';
export const INSPECTORS_SECTION_KNM_LABEL = 'Сведения о должностных лицах, участвующих в КНМ';
export const INSPECTORS_SECTION_PM_LABEL =
  'Сведения о должностных лицах контрольных (надзорных) органов, участвующих в соответствующих профилактических мероприятиях';

const getFullName = (inspector: IKnmErknmFormInspector): string | undefined =>
  inspector.knoInspector?.value.fullName ?? inspector.fullName;

const getNotInFieldError = (fieldLabel: string): string =>
  `Должностное лицо, выбранное в поле, должно присутствовать в поле «${fieldLabel}»`;

/** Читает текст ошибки поля knoInspectors из структуры ошибок final-form (строка или ARRAY_ERROR). */
export const getKnoInspectorsArrayErrorMessage = (knoInspectorsError: unknown): string | undefined => {
  if (typeof knoInspectorsError === 'string') {
    return knoInspectorsError;
  }

  const arrayError = (knoInspectorsError as { [key: string]: unknown } | undefined)?.[ARRAY_ERROR];

  return typeof arrayError === 'string' ? arrayError : undefined;
};

/** Валидатор поля «ФИО подписавшего»: ФИО должно совпадать с одним из ДЛ в knoInspectors. */
export const makeSignerInInspectorsValidator = (
  knoInspectors: IKnmErknmFormInspector[] | undefined,
  inspectorsLabel: string,
) => (signerFullName: string | undefined): string | undefined => {
  if (!signerFullName) {
    return undefined;
  }

  const isSignerInList = knoInspectors?.some(item => getFullName(item)?.normalize() === signerFullName.normalize());

  return isSignerInList ? undefined : getNotInFieldError(inspectorsLabel);
};

/**
 * Field-level валидатор списка ДЛ акта (FieldArray validate): каждое ДЛ акта должно
 * присутствовать (по ФИО) среди должностных лиц паспорта.
 */
export const makeKnoInspectorsInPassportValidator = (
  passportInspectorsFieldName: string,
  inspectorsSectionLabel: string,
) => (knoInspectors: IKnmErknmFormInspector[] | undefined, allValues: object): string | undefined => {
  if (!knoInspectors?.length) {
    return undefined;
  }

  const passportInspectors = (getIn(allValues, passportInspectorsFieldName) ?? []) as IKnmErknmFormInspector[];
  const passportFullNames = new Set(passportInspectors.map(inspector => inspector.fullName?.normalize()));

  const hasInspectorNotInPassport = knoInspectors.some(item => {
    const fullName = getFullName(item);

    return !!fullName && !passportFullNames.has(fullName?.normalize());
  });

  return hasInspectorNotInPassport ? getNotInFieldError(inspectorsSectionLabel) : undefined;
};
